import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

export const notificationsKeys = {
  all: (fleetId: number) => ["notifications", "idFleet", fleetId] as const,
};

export const useGetAllNotification = (fleetId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: notificationsKeys.all(fleetId),
    queryFn: async (): Promise<TNotification[]> => {
      // NOTE: the axios instance returns the FULL AxiosResponse (no unwrap
      // interceptor) — the previous code spread the response object itself,
      // which threw "not iterable" and silently emptied the table.
      const [resTrue, resFalse] = await Promise.all([
        axios.get(`/notification/list`, { params: { is_read: true } }),
        axios.get(`/notification/list`, { params: { is_read: false } }),
      ]);
      const dataTrue: TNotification[] = Array.isArray(resTrue.data) ? resTrue.data : [];
      const dataFalse: TNotification[] = Array.isArray(resFalse.data) ? resFalse.data : [];
      return [...dataTrue, ...dataFalse];
    },
  });

  return { notifications: data ?? [], ...res };
};

export const useReadNotification = (fleetId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notification_id: number) => {
      // The backend reads $params['notification_id']; posting the bare
      // number left it unset — which marked EVERY notification as read.
      const data = await axios.post(`/notification/read`, { notification_id });
      return data.data?.notification;
    },
    onSuccess: (_) => {
      const keys = notificationsKeys.all(fleetId);
      queryClient.refetchQueries({ queryKey: keys });
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useRelaunchNotification = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (notification_id: number) => {
      const res = await axios.post(`/notification/relaunch`, { notification_id });
      return res.data;
    },
    onSuccess: (_) => {
      toast.success(t("notification.relaunchSuccess", "Email sent to collaborator"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ||
          t("notification.relaunchError", "Failed to send email")
      );
    },
  });
};

/**
 * Relaunch the collaborators behind a set of notifications.
 *
 * The API reports per-id outcomes instead of failing the batch, so one stale
 * notification pointing at a removed collaborator does not cancel the others.
 * The toast reflects that: a partial success is reported as partial rather
 * than as blanket success.
 */
export const useRelaunchBulkNotification = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await axios.post(`/notification/relaunch-bulk`, { ids });
      return res.data as {
        success: boolean;
        sent: number;
        failed: { id: number; message: string }[];
        total: number;
      };
    },
    onSuccess: (data) => {
      if (data.failed?.length) {
        toast.success(
          t("notification.relaunchPartial", {
            defaultValue: "{{sent}} sur {{total}} e-mails envoyés",
            sent: data.sent,
            total: data.total,
          })
        );
        return;
      }
      toast.success(
        t("notification.relaunchBulkSuccess", {
          defaultValue: "{{count}} e-mail(s) envoyé(s)",
          count: data.sent,
        })
      );
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ||
          t("notification.relaunchError", "Failed to send email")
      );
    },
  });
};

/**
 * Delete a specific set of notifications.
 *
 * Takes the ids explicitly. This previously sent `DELETE /notification/delete`
 * with no body, which the API read as "delete every notification this user
 * has" — so a bulk delete of three selected rows would have wiped the lot.
 * The API now rejects an id-less request instead of assuming "all".
 */
export const useDeleteBulkNotification = (fleetId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      // axios carries a DELETE body under `data`, not as the second argument.
      const res = await axios.delete(`/notification/delete`, { data: { ids } });
      return res.data;
    },
    onSuccess: (_, ids) => {
      const keys = notificationsKeys.all(fleetId);
      queryClient.refetchQueries({ queryKey: keys });
      toast.success(
        t("notification.deleteBulkSuccess", {
          defaultValue: "{{count}} notification(s) supprimée(s)",
          count: ids.length,
        })
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useDeleteOneNotification = (
  fleetId: number,
  notificationId: number
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.delete(`/notification/delete/${notificationId}`);
      return data.data;
    },
    onSuccess: (_) => {
      const keys = notificationsKeys.all(fleetId);
      const prev = queryClient.getQueryData<TNotification[]>(keys);
      if (prev) {
        const res = prev.filter(
          (notification) => notification.id !== notificationId
        );
        queryClient.setQueryData<TNotification[]>(keys, res);
      }
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });
};
